/**
 * Valoraa: keyboard fixes for the cart drawer and the cart page (loaded by snippets/cart-drawer.liquid
 * and sections/main-cart.liquid). Horizon core files are untouched; everything runs from document-level
 * listeners, so it survives the Section Rendering API re-renders.
 *
 * 1. Enter in a line's quantity field updates the quantity in place. The field sits inside #cart-form,
 *    so without this Enter submits the form through the CHECK OUT button (name="checkout") and sends
 *    the shopper to checkout. Horizon commits the typed quantity on blur, so we replay that blur
 *    (focus stays in the field) and swallow the next real blur if the value has not changed since.
 * 2. After "Remove", focus stays inside the cart. When the re-render leaves focus on <body> (the removed
 *    row, or the whole list when the cart empties), it moves to the Remove button now at that position
 *    (or the previous one), and on an empty cart to the empty-state heading (or its button).
 */

/**
 * The line and value last committed with Enter. Kept outside the DOM because the re-render morphs
 * the input's attributes back to the server's.
 * @type {{ line: string, value: string } | null}
 */
let committed = null;
const WAIT_LIMIT = 6000;

/** @param {EventTarget | null} target */
const cartQtyInput = (target) =>
  target instanceof HTMLInputElement && target.type === 'number' && target.closest('cart-items-component .quantity-selector')
    ? target
    : null;

/** @param {KeyboardEvent} event */
const onKeydown = (event) => {
  if (event.key !== 'Enter' || event.isComposing) return;
  const input = cartQtyInput(event.target);
  if (!input) return;

  event.preventDefault();
  committed = { line: input.dataset.cartLine ?? '', value: input.value };
  // Horizon's quantity selector listens for blur through its document-level delegation
  input.dispatchEvent(new FocusEvent('blur'));
  input.select();
};

/** @param {FocusEvent} event */
const onBlur = (event) => {
  if (!event.isTrusted) return;
  const input = cartQtyInput(event.target);
  if (!input || !committed) return;

  const unchanged = committed.line === (input.dataset.cartLine ?? '') && committed.value === input.value;
  committed = null;
  // Already committed with Enter: skip Horizon's blur handler so the same quantity is not posted twice
  if (unchanged) event.stopImmediatePropagation();
};

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const frame = () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

/** @param {string} sectionId */
const findItems = (sectionId) =>
  /** @type {HTMLElement | null} */ (
    document.querySelector(`cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`)
  );

/** @param {Element | null} element */
const needsRescue = (element) =>
  !element ||
  element === document.body ||
  !element.isConnected ||
  !!element.closest('.removing') ||
  !(element instanceof HTMLElement) ||
  element.offsetParent === null;

/** @param {HTMLElement | null} element */
const focus = (element) => {
  if (!element) return false;
  if (!element.matches('a[href], button, input, [tabindex]')) element.setAttribute('tabindex', '-1');
  element.focus({ preventScroll: false });
  return document.activeElement === element;
};

/**
 * @param {string} sectionId
 * @param {number} index - position of the removed row among the Remove buttons
 */
const restoreFocus = async (sectionId, index) => {
  const started = Date.now();
  await wait(60);
  // Horizon marks the list busy while /cart/change runs and the section morphs
  while (Date.now() - started < WAIT_LIMIT) {
    const items = findItems(sectionId);
    if (items && !items.classList.contains('cart-items-disabled') && !items.querySelector('.removing')) break;
    await wait(80);
  }
  // The page and other sections can still morph after the list is re-enabled, so keep watching briefly
  const until = Date.now() + 1500;
  while (Date.now() < until) {
    await frame();
    if (needsRescue(document.activeElement)) rescue(sectionId, index);
    await wait(100);
  }
};

/**
 * @param {string} sectionId
 * @param {number} index
 */
const rescue = (sectionId, index) => {
  const items = findItems(sectionId);
  if (!items) return;

  const removes = /** @type {HTMLElement[]} */ ([...items.querySelectorAll('.cart-items__remove')]).filter(
    (button) => button.offsetParent !== null && !button.closest('.removing')
  );
  if (removes.length) {
    focus(removes[Math.min(index, removes.length - 1)] ?? null);
    return;
  }

  const scope = items.closest('dialog') ?? items.closest('.shopify-section') ?? items;
  const heading = /** @type {HTMLElement | null} */ (
    items.querySelector('.vb-cart-empty__heading') ?? scope.querySelector('h1, .cart-drawer__heading')
  );
  if (focus(heading)) return;
  focus(/** @type {HTMLElement | null} */ (items.querySelector('.vb-cart-empty__button')));
};

/** @param {MouseEvent} event */
const onClick = (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const button = target?.closest('.cart-items__remove');
  const items = button?.closest('cart-items-component');
  if (!button || !(items instanceof HTMLElement) || !items.dataset.sectionId) return;

  const index = [...items.querySelectorAll('.cart-items__remove')].indexOf(button);
  restoreFocus(items.dataset.sectionId, Math.max(index, 0));
};

if (!(/** @type {any} */ (window).vbCartA11y)) {
  /** @type {any} */ (window).vbCartA11y = true;
  document.addEventListener('keydown', onKeydown, { capture: true });
  // Window capture runs before Horizon's document-level capture delegation
  window.addEventListener('blur', onBlur, { capture: true });
  document.addEventListener('click', onClick, { capture: true });
}
